import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { ArrowUp, ArrowDown, ArrowUpRight, ArrowDownRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TEAL = "#b8d4d0";
const ACCENT = "#DDFECE";
const ACCENT2 = "#CDFFD9";
const RED = "#c47070";
const MUTED = "rgba(184,212,208,0.45)";
const BG = "var(--pord-bg, #222518)";

export default function WeeklySummaryV3() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [catFilter, setCatFilter] = useState("Money Market");
  const [search, setSearch] = useState("");
  const [hoverRow, setHoverRow] = useState(null);
  const [pickedCodes, setPickedCodes] = useState([]);
  const [readMeOI, setReadMeOI] = useState(false);
  const [readMeNet, setReadMeNet] = useState(false);

  useEffect(() => {
    let retries = 0;
    const load = () => {
      axios.get(`${API}/api/cot-pro/v3-summary`)
        .then(r => { setData(r.data); setLoading(false); })
        .catch(err => {
          if (err.response?.status === 503 && retries < 30) { retries++; setTimeout(load, 2000); return; }
          setLoading(false);
        });
    };
    load();
  }, []);

  const categories = useMemo(() => [...new Set(data.map(r => r.category).filter(Boolean))], [data]);
  const sorted = useMemo(() => {
    let rows = [...data];
    if (pickedCodes.length > 0) rows = rows.filter(r => pickedCodes.includes(r.code));
    else {
      if (catFilter) rows = rows.filter(r => r.category === catFilter);
      if (search) { const q = search.toLowerCase(); rows = rows.filter(r => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q)); }
    }
    if (sortKey) rows.sort((a, b) => { const av = a[sortKey] ?? -Infinity, bv = b[sortKey] ?? -Infinity; return sortDir === "desc" ? bv - av : av - bv; });
    return rows;
  }, [data, sortKey, sortDir, catFilter, search, pickedCodes]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (loading) return <div style={{ color: TEAL, textAlign: "center", padding: 60, fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>Loading V3 Summary...</div>;

  return (
    <div data-testid="v3-weekly-summary" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* ReadMe row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <PillBtn active={readMeOI} onClick={() => { setReadMeOI(v => !v); setReadMeNet(false); }} label="READ ME: OI" testId="read-me-oi-btn" />
        <PillBtn active={readMeNet} onClick={() => { setReadMeNet(v => !v); setReadMeOI(false); }} label="READ ME: NET%OI" testId="read-me-net-btn" />
        <a href="https://pordapp.substack.com/p/weekly-summary-07-21-2026" target="_blank" rel="noopener noreferrer"
          style={{ color: TEAL, fontSize: 12, fontWeight: 400, textDecoration: "underline" }}>
          Example-Weekly Summary
        </a>
        <span style={{ marginLeft: "auto", fontSize: 13, color: TEAL, letterSpacing: "0.06em" }}>Pctile : 52-week lookback</span>
      </div>
      {readMeOI && <ReadMeOIPanel />}
      {readMeNet && <ReadMeNetPanel />}

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <input data-testid="v3-search" placeholder="Search by name or code..." value={search}
          onChange={e => setSearch(e.target.value)} className="pord-input" style={{ maxWidth: 220, fontSize: 12 }} />
        <select data-testid="v3-cat-filter" className="pord-select" style={{ fontSize: 12 }} value={catFilter}
          onChange={e => setCatFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <ContractPicker data={data} pickedCodes={pickedCodes} setPickedCodes={setPickedCodes} />
        <span style={{ fontSize: 11, color: "#fff" }}>{sorted.length} contracts</span>
        <span style={{ marginLeft: "auto", fontSize: 13, color: "#fff", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 400 }}>Flow Sentiment</span>
      </div>

      {/* Chips */}
      {pickedCodes.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, alignItems: "center" }}>
          {pickedCodes.map(code => {
            const item = data.find(r => r.code === code);
            return (
              <span key={code} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, background: "rgba(184,212,208,0.10)", border: "1px solid rgba(184,212,208,0.20)", fontSize: 11, color: TEAL }}>
                {item ? `${item.num}. ${item.name?.replace(/ - .*/, "")}` : code}
                <button onClick={() => setPickedCodes(prev => prev.filter(p => p !== code))} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 13, cursor: "pointer", padding: 0 }}>×</button>
              </span>
            );
          })}
          <button onClick={() => setPickedCodes([])} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 11, cursor: "pointer", textDecoration: "underline" }}>Clear all</button>
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 230px)", borderRadius: 8, border: "1px solid rgba(184,212,208,0.08)" }}>
        <table data-testid="v3-summary-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            {/* Group header row */}
            <tr style={{ position: "sticky", top: 0, zIndex: 11, background: BG }}>
              <th colSpan={5} style={{ ...groupHdr, background: "rgba(184,212,208,0.06)", borderRight: "2px solid rgba(184,212,208,0.18)" }}>Contract</th>
              <th colSpan={7} style={{ ...groupHdr, background: "rgba(205,255,217,0.05)", borderRight: "2px solid rgba(184,212,208,0.18)" }}>Flows</th>
              <th colSpan={2} style={{ ...groupHdr, background: "rgba(221,254,206,0.13)", color: "#DDFECE", fontWeight: 600, letterSpacing: "0.12em", borderBottom: "2px solid rgba(221,254,206,0.25)" }}>Summary</th>
            </tr>
            <tr style={{ position: "sticky", top: 28, zIndex: 10, background: BG, boxShadow: "0 1px 0 rgba(184,212,208,0.10)" }}>
              <Th label="Contract" align="left" group={1} />
              <ThSort label="Open Interest" sk="oi" active={sortKey} dir={sortDir} onClick={toggleSort} group={1} />
              <ThSort label="w/w OI" sk="oi_change" active={sortKey} dir={sortDir} onClick={toggleSort} group={1} />
              <ThSort label={"w/w\n(Extreme Add / Close)"} sk="pct_change_oi" active={sortKey} dir={sortDir} onClick={toggleSort} group={1} />
              <ThSort label="Est. $ Exposure" sk="d04_exposure" active={sortKey} dir={sortDir} onClick={toggleSort} group={1} last />
              <Th label="Flow Initiator" group={2} />
              <Th label="6M Slope" align="center" group={2} />
              <Th label="6M Flow" group={2} />
              <Th label="6M Beg. %OI" align="right" group={2} />
              <Th label="PriorWk %OI" align="right" group={2} />
              <Th label="Latest %OI" align="right" group={2} />
              <Th label="w/w (Long Liquidated / Short Covering)" group={2} last />
              <Th label="6M" align="center" group={3} />
              <Th label="WoW" align="center" group={3} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, idx) => <ContractRow key={r.code} r={r} hovered={hoverRow === r.code} onHover={setHoverRow} odd={idx % 2 === 1} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContractRow({ r, hovered, onHover, odd }) {
  // Skip contracts with no w/w OI data
  if (r.oi_change == null) return null;
  const altBg = odd ? "rgba(184,212,208,0.045)" : "transparent";
  const hBg = hovered ? "rgba(221,254,206,0.055)" : altBg;
  return (
    <>
      {/* Spacer row between contracts */}
      <tr><td colSpan={14} style={{ padding: 0, height: 8, background: "transparent", border: "none" }}></td></tr>
      {/* Largest */}
      <tr onMouseEnter={() => onHover(r.code)} onMouseLeave={() => onHover(null)}>
        <td rowSpan={2} style={{ ...ce, minWidth: 130, verticalAlign: "middle", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", borderRight: bd }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ color: "#E0E4DA", fontSize: 12, minWidth: 20 }}>{r.num}</span>
            <span style={{ color: "#e8e8e8", fontSize: 14 }}>{r.name?.replace(/ - .*/, "")}</span>
          </div>
        </td>
        <td rowSpan={2} style={{ ...ce, ...nm, borderRight: bd, verticalAlign: "middle", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)" }}>{fmtNum(r.oi)}</td>
        <td rowSpan={2} style={{ ...ce, ...nm, borderRight: bd, verticalAlign: "middle", color: r.oi_change >= 0 ? ACCENT : RED, background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)" }}>{fmtSigned(r.oi_change)}</td>
        <td rowSpan={2} style={{ ...ce, ...nm, borderRight: bd, verticalAlign: "middle", color: r.pct_change_oi != null && r.pct_change_oi < 0 ? RED : ACCENT2, background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)" }}>
          {r.pct_change_oi != null ? `${r.pct_change_oi > 0 ? "+" : ""}${r.pct_change_oi.toFixed(1)}%` : ""}
        </td>
        <td rowSpan={2} style={{ ...ce, ...nm, borderRight: gbd, verticalAlign: "middle", color: "rgba(184,212,208,0.85)", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)" }}>{fmtDollar(r.d04_exposure)}</td>
        <td style={{ ...ce, borderRight: bd, whiteSpace: "nowrap", background: hBg, padding: "4px 8px" }}>
          <span style={{ color: "rgba(184,212,208,0.65)", fontSize: 15 }}>{r.largest_label}</span>
          <span style={{ color: "#808080", fontSize: 14, marginLeft: 8, fontWeight: 300 }}>Largest</span>
        </td>
        <td style={{ ...ce, textAlign: "center", borderRight: bd, background: hBg, padding: "4px 8px" }}><SlopeArrow val={r.largest_slope} /></td>
        <td style={{ ...ce, borderRight: bd, minWidth: 78, background: hBg, padding: "4px 8px" }}><Sparkline data={r.largest_spark} color={ACCENT2} /></td>
        <td style={{ ...ce, ...nm, borderRight: bd, background: hBg, padding: "4px 8px" }}>{r.largest_6m_beg != null ? `${r.largest_6m_beg.toFixed(2)}%` : ""}</td>
        <td style={{ ...ce, ...nm, borderRight: bd, color: "rgba(184,212,208,0.5)", background: hBg, padding: "4px 8px" }}>{r.largest_prior_pctoi != null ? `${r.largest_prior_pctoi.toFixed(2)}%` : ""}</td>
        <td style={{ ...ce, ...nm, borderRight: bd, color: "#f0f0f0", background: hBg, padding: "4px 8px" }}>{r.largest_net_pctoi != null ? `${r.largest_net_pctoi.toFixed(2)}%` : ""}</td>
        <td style={{ ...ce, borderRight: gbd, whiteSpace: "nowrap", background: hBg, minWidth: 120, textAlign: "center", padding: "4px 8px" }}><WowLabel text={r.largest_wow} /></td>
        <td rowSpan={2} style={{ ...ce, textAlign: "center", borderRight: bd, verticalAlign: "middle", background: hovered ? "rgba(221,254,206,0.10)" : "rgba(221,254,206,0.045)", borderBottom: "1px solid rgba(184,212,208,0.06)" }}><SentimentArrow text={r.flow_6m} /></td>
        <td rowSpan={2} style={{ ...ce, textAlign: "center", verticalAlign: "middle", background: hovered ? "rgba(221,254,206,0.10)" : "rgba(221,254,206,0.045)", borderBottom: "1px solid rgba(184,212,208,0.06)" }}><SentimentArrow text={r.flow_wow} /></td>
      </tr>
      {/* 2nd — tighter to Largest */}
      <tr onMouseEnter={() => onHover(r.code)} onMouseLeave={() => onHover(null)}>
        <td style={{ ...ce, borderRight: bd, whiteSpace: "nowrap", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", padding: "4px 8px" }}>
          <span style={{ color: "rgba(184,212,208,0.50)", fontSize: 15 }}>{r.second_label}</span>
          <span style={{ color: "#808080", fontSize: 14, marginLeft: 8, fontWeight: 300 }}>2nd</span>
        </td>
        <td style={{ ...ce, textAlign: "center", borderRight: bd, background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", padding: "4px 8px" }}><SlopeArrow val={r.second_slope} /></td>
        <td style={{ ...ce, borderRight: bd, background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", padding: "4px 8px" }}><Sparkline data={r.second_spark} color="rgba(205,255,217,0.4)" /></td>
        <td style={{ ...ce, ...nm, borderRight: bd, color: "rgba(184,212,208,0.5)", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", padding: "4px 8px" }}>{r.second_6m_beg != null ? `${r.second_6m_beg.toFixed(2)}%` : ""}</td>
        <td style={{ ...ce, ...nm, borderRight: bd, color: "rgba(184,212,208,0.4)", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", padding: "4px 8px" }}>{r.second_prior_pctoi != null ? `${r.second_prior_pctoi.toFixed(2)}%` : ""}</td>
        <td style={{ ...ce, ...nm, borderRight: bd, color: "rgba(184,212,208,0.65)", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", padding: "4px 8px" }}>{r.second_net_pctoi != null ? `${r.second_net_pctoi.toFixed(2)}%` : ""}</td>
        <td style={{ ...ce, borderRight: gbd, whiteSpace: "nowrap", background: hBg, borderBottom: "1px solid rgba(184,212,208,0.06)", minWidth: 120, textAlign: "center", padding: "4px 8px" }}><WowLabel text={r.second_wow} /></td>
      </tr>
    </>
  );
}

/* ─── Sub-components ───────────────────────────────────────────── */
function PillBtn({ active, onClick, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 18px", borderRadius: 999, background: active ? TEAL : "rgba(184,212,208,0.12)", border: `1px solid ${TEAL}`, color: active ? "#1a1e14" : TEAL, fontSize: 12, fontWeight: 500, letterSpacing: "0.06em", cursor: "pointer", transition: "all 200ms", boxShadow: "0 0 10px rgba(184,212,208,0.15)", fontFamily: "'DM Sans', sans-serif" }}>
      {label}
    </button>
  );
}

const groupHdr = { padding: "5px 8px", textAlign: "center", color: TEAL, fontSize: 11, fontWeight: 500, letterSpacing: "0.10em", textTransform: "uppercase", borderBottom: "1px solid rgba(184,212,208,0.12)", fontFamily: "'DM Sans', sans-serif" };
const GROUP_BG = { 1: "rgba(184,212,208,0.03)", 2: "rgba(205,255,217,0.025)", 3: "rgba(221,254,206,0.08)" };

function Th({ label, align = "left", sub, group, last }) {
  const groupBorder = last ? { borderRight: "2px solid rgba(184,212,208,0.18)" } : {};
  return (
    <th style={{ padding: "8px 8px", textAlign: align, color: "#fff", fontSize: 13, fontWeight: 400, whiteSpace: "pre-line", borderBottom: "1px solid rgba(184,212,208,0.12)", letterSpacing: "0.06em", textTransform: "uppercase", background: group ? GROUP_BG[group] : BG, position: "sticky", top: 28, zIndex: 10, ...groupBorder }}>
      {label}
      {sub && <div style={{ fontSize: 8, color: "#fff", letterSpacing: "0.08em", fontWeight: 300, marginTop: 1 }}>{sub}</div>}
    </th>
  );
}

function ThSort({ label, sk, active, dir, onClick, group, last }) {
  const isActive = active === sk;
  const groupBorder = last ? { borderRight: "2px solid rgba(184,212,208,0.18)" } : {};
  return (
    <th onClick={() => onClick(sk)}
      style={{ padding: "8px 8px", textAlign: "right", color: isActive ? "#fff" : "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 400, whiteSpace: "pre-line", cursor: "pointer", userSelect: "none", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid rgba(184,212,208,0.12)", background: isActive ? "rgba(154,173,168,0.10)" : (group ? GROUP_BG[group] : BG), position: "sticky", top: 28, zIndex: 10, transition: "background 200ms", ...groupBorder }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        <SortIcon active={isActive} dir={dir} />
      </span>
    </th>
  );
}

function SortIcon({ active, dir }) {
  const ac = "#fff", id = "rgba(154,173,168,0.5)";
  if (!active) return <svg width="10" height="14" viewBox="0 0 10 14"><path d="M5 1L9 6H1Z" fill={id} /><path d="M5 13L9 8H1Z" fill={id} /></svg>;
  return <svg width="10" height="14" viewBox="0 0 10 14"><path d="M5 1L9 6H1Z" fill={dir === "asc" ? ac : "rgba(154,173,168,0.2)"} /><path d="M5 13L9 8H1Z" fill={dir === "desc" ? ac : "rgba(154,173,168,0.2)"} /></svg>;
}

function SlopeArrow({ val }) {
  if (val == null) return <span style={{ color: "rgba(184,212,208,0.12)" }}>—</span>;
  if (val > 0.01) return <ArrowUp size={20} strokeWidth={2.8} style={{ color: ACCENT2 }} />;
  if (val < -0.01) return <ArrowDown size={20} strokeWidth={2.8} style={{ color: RED }} />;
  return <span style={{ color: "rgba(184,212,208,0.18)" }}>—</span>;
}

function SentimentArrow({ text }) {
  if (!text || text === "-") return <span style={{ color: "rgba(184,212,208,0.12)" }}>—</span>;
  const map = { "Both UP": { Icon: ArrowUp, color: ACCENT2 }, "Both DOWN": { Icon: ArrowDown, color: RED }, "Mix_SkewedUP": { Icon: ArrowUpRight, color: ACCENT }, "Mix_SkewedDOWN": { Icon: ArrowDownRight, color: RED } };
  const m = map[text];
  if (!m) return <span style={{ color: MUTED, fontSize: 11 }}>{text}</span>;
  return <m.Icon size={22} strokeWidth={2.8} style={{ color: m.color }} />;
}

function WowLabel({ text }) {
  if (!text || text === "-") return <span style={{ color: "rgba(184,212,208,0.25)", fontSize: 11, fontStyle: "italic" }}>Non-Directional</span>;
  return <span style={{ color: TEAL, fontSize: 15, fontWeight: 400 }}>w/w {text}</span>;
}

function Sparkline({ data, color = ACCENT2 }) {
  if (!data || data.length < 2) return null;
  const valid = data.filter(v => v != null);
  if (valid.length < 2) return null;
  const min = Math.min(...valid), max = Math.max(...valid), range = max - min || 1;
  const w = 78, h = 24;
  const pts = valid.map((v, i) => `${(i / (valid.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  return <svg width={w} height={h} style={{ display: "block" }}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.3" strokeLinejoin="round" /><circle cx={w} cy={h - ((valid[valid.length - 1] - min) / range) * h} r="2.5" fill={color} /></svg>;
}

function ContractPicker({ data, pickedCodes, setPickedCodes }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => { const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  const filtered = useMemo(() => { let items = [...data].sort((a, b) => (a.num || 0) - (b.num || 0)); if (q) { const lower = q.toLowerCase(); items = items.filter(r => r.name.toLowerCase().includes(lower) || String(r.num).includes(lower) || r.code.includes(lower)); } return items; }, [data, q]);
  const toggle = code => setPickedCodes(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button data-testid="v3-contract-picker-btn" onClick={() => setOpen(!open)} className="pord-select" style={{ fontSize: 12, padding: "6px 14px", borderRadius: 6, cursor: "pointer", background: pickedCodes.length > 0 ? "rgba(184,212,208,0.08)" : "transparent" }}>
        {pickedCodes.length > 0 ? `SELECT CONTRACT (${pickedCodes.length})` : "SELECT CONTRACT"}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 200, background: "#2a2e22", border: "1px solid rgba(184,212,208,0.18)", borderRadius: 10, width: 360, maxHeight: 440, display: "flex", flexDirection: "column", boxShadow: "0 12px 40px rgba(0,0,0,0.55)" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(184,212,208,0.08)" }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..." style={{ width: "100%", padding: "7px 10px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(184,212,208,0.12)", borderRadius: 6, color: "#fff", outline: "none" }} />
          </div>
          {pickedCodes.length > 0 && <button onClick={() => setPickedCodes([])} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", background: "rgba(184,212,208,0.06)", border: "none", borderBottom: "1px solid rgba(184,212,208,0.08)", color: TEAL, cursor: "pointer" }}>Clear all ({pickedCodes.length})</button>}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.map(item => {
              const sel = pickedCodes.includes(item.code);
              return (
                <button key={item.code} onClick={() => toggle(item.code)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "7px 12px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: sel ? "rgba(184,212,208,0.10)" : "transparent", border: "none", color: sel ? "#fff" : "rgba(255,255,255,0.6)", cursor: "pointer", transition: "background 100ms" }} onMouseEnter={e => { if (!sel) e.currentTarget.style.background = "rgba(184,212,208,0.06)"; }} onMouseLeave={e => { if (!sel) e.currentTarget.style.background = "transparent"; }}>
                  <span style={{ width: 16, height: 16, borderRadius: 3, flexShrink: 0, border: sel ? "none" : "1px solid rgba(184,212,208,0.3)", background: sel ? TEAL : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#1a1e14", fontWeight: 700 }}>{sel ? "✓" : ""}</span>
                  <span style={{ color: TEAL, minWidth: 24 }}>{item.num}.</span>
                  <span>{item.name?.replace(/ - .*/, "")}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── ReadMe Panels (full detail from old WeeklySummary) ─────────── */
function ReadMeOIPanel() {
  return (
    <div style={{ marginBottom: 16, padding: "24px 28px", background: "rgba(255,255,255,0.025)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)", fontSize: 15, color: "rgba(255,255,255,0.8)", lineHeight: 1.8 }}>
      <div style={{ marginBottom: 14 }}><span style={{ color: "#fff", fontWeight: 600, fontSize: 16 }}>Open Interest (OI)</span> is the total number of outstanding futures contracts. OI counts 1 Long and 1 Short as 1 Open Interest (OI), not the sum of all longs and shorts. The aggregate of all long open interest is equal to the aggregate of all short open interest.</div>
      <div style={{ marginBottom: 14, padding: "12px 16px", background: "rgba(184,212,208,0.04)", borderRadius: 8, border: "1px solid rgba(184,212,208,0.08)", fontSize: 14, color: "rgba(255,255,255,0.68)" }}>
        <span style={{ color: TEAL, fontWeight: 500 }}>Spread in the report context:</span> A short position and a long position held in one contract name by the same market participant. This spread includes but not limited to contracts initiated for spread trade strategies.
      </div>
      <div style={{ marginBottom: 14, padding: "12px 16px", background: "rgba(184,212,208,0.04)", borderRadius: 8, border: "1px solid rgba(184,212,208,0.08)", fontSize: 13, color: "rgba(255,255,255,0.60)", fontStyle: "italic" }}>
        <span style={{ color: TEAL, fontWeight: 500, fontStyle: "normal" }}>Example:</span> 2,000 long contracts and 1,500 short contracts of Eurodollar futures held by a market participant, 500 contracts will appear in the "Long" category and 1,500 contracts will appear in the "Spreading" category. These figures do not include intermarket spreading, such as spreading Eurodollar futures against Treasury Note futures.
      </div>
      <div style={{ marginBottom: 16, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Source: <a href="https://www.cftc.gov/MarketReports/CommitmentsofTraders/ExplanatoryNotes/index.htm" target="_blank" rel="noopener noreferrer" style={{ color: TEAL }}>CFTC Explanatory Notes</a></div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 600, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
              <th style={{ textAlign: "right", padding: "8px 12px" }}></th>
              <th style={{ textAlign: "center", padding: "8px 8px" }}></th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#fff", fontWeight: 500, fontSize: 13 }}>Spread</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#fff", fontWeight: 500, fontSize: 13 }}>Outright</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#DAF2D0", fontWeight: 500, fontSize: 13 }}>Long Leg</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#DAF2D0", fontWeight: 500, fontSize: 13 }}>Short Leg</th>
            </tr>
          </thead>
          <tbody>
            {[
              { name: "Dealer", spread: 20, outL: 30, outS: 35, longLeg: 50, shortLeg: 55 },
              { name: "Asset Managers - AM", spread: 50, outL: 45, outS: 30, longLeg: 95, shortLeg: 80 },
              { name: "Leveraged Funds - LM", spread: 80, outL: 40, outS: 65, longLeg: 120, shortLeg: 145 },
              { name: "Other Reportables - OthRept", spread: 10, outL: 25, outS: 20, longLeg: 35, shortLeg: 30 },
              { name: "Non Reportables - NonRept", spread: null, outL: 25, outS: 15, longLeg: 25, shortLeg: 15 },
            ].map(r => (
              <React.Fragment key={r.name}>
                <tr style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <td rowSpan={2} style={{ textAlign: "right", padding: "5px 12px", color: "#fff", fontSize: 13, fontWeight: 400, verticalAlign: "middle" }}>{r.name}</td>
                  <td style={{ textAlign: "center", padding: "4px 8px", color: "#fff", fontSize: 12, fontWeight: 400 }}>L</td>
                  <td rowSpan={2} style={{ textAlign: "center", padding: "4px 12px", color: "#fff", fontSize: 14 }}>{r.spread ?? ""}</td>
                  <td style={{ textAlign: "center", padding: "4px 12px", color: "#fff", fontSize: 14 }}>{r.outL}</td>
                  <td style={{ textAlign: "center", padding: "4px 12px", color: "#DAF2D0", fontSize: 14, background: "rgba(218,242,208,0.05)" }}>{r.longLeg}</td>
                  <td style={{ textAlign: "center", padding: "4px 12px" }}></td>
                </tr>
                <tr>
                  <td style={{ textAlign: "center", padding: "4px 8px", color: "#fff", fontSize: 12, fontWeight: 400 }}>S</td>
                  <td style={{ textAlign: "center", padding: "4px 12px", color: "#fff", fontSize: 14 }}>{r.outS}</td>
                  <td style={{ textAlign: "center", padding: "4px 12px" }}></td>
                  <td style={{ textAlign: "center", padding: "4px 12px", color: "#DAF2D0", fontSize: 14, background: "rgba(218,242,208,0.05)" }}>{r.shortLeg}</td>
                </tr>
              </React.Fragment>
            ))}
            <tr style={{ borderTop: "2px solid rgba(255,255,255,0.15)" }}>
              <td colSpan={2} style={{ padding: "6px 12px" }}></td>
              <td colSpan={2} style={{ textAlign: "right", padding: "6px 12px", color: "#fff", fontSize: 13, fontWeight: 500 }}>Open Interest (OI) =</td>
              <td style={{ textAlign: "center", padding: "6px 12px", color: "#fff", fontSize: 14, fontWeight: 600 }}>325</td>
              <td style={{ textAlign: "center", padding: "6px 12px", color: "#fff", fontSize: 14, fontWeight: 600 }}>325</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReadMeNetPanel() {
  return (
    <div style={{ marginBottom: 16, padding: "24px 28px", background: "rgba(255,255,255,0.025)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)", fontSize: 15, color: "rgba(255,255,255,0.8)", lineHeight: 1.8 }}>
      <div style={{ marginBottom: 14 }}>After removing spreading, what remains are the <span style={{ color: "#fff", fontWeight: 600 }}>outright long and short</span> positions held by each group. Netting longs and shorts (L − S) shows how participants are generally positioned in that futures contract.</div>
      <div style={{ marginBottom: 18 }}>Asset Managers typically reflect more structural hedging and allocation flows, while Leveraged Funds tend to express strategies calibrated to the current market regime.</div>
      <div style={{ overflowX: "auto", marginBottom: 18 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 600, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
              <th style={{ textAlign: "right", padding: "8px 12px" }}></th>
              <th style={{ textAlign: "center", padding: "8px 8px" }}></th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#fff", fontWeight: 500, fontSize: 13 }}>Spread</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#fff", fontWeight: 500, fontSize: 13 }}>Outright</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#fff", fontWeight: 600, fontSize: 13 }}>Net</th>
              <th style={{ textAlign: "center", padding: "8px 12px", color: "#DAF2D0", fontWeight: 600, fontSize: 13 }}>Net % OI</th>
            </tr>
          </thead>
          <tbody>
            {[
              { name: "Dealer", outL: 30, outS: 35, net: -5, pct: "-1.5%" },
              { name: "Asset Managers - AM", outL: 45, outS: 30, net: 15, pct: "4.6%" },
              { name: "Leveraged Funds - LM", outL: 40, outS: 65, net: -25, pct: "-7.7%" },
              { name: "Other Reportables - OthRept", outL: 25, outS: 20, net: 5, pct: "1.5%" },
              { name: "Non Reportables - NonRept", outL: 25, outS: 15, net: 10, pct: "3.1%" },
            ].map(r => (
              <React.Fragment key={r.name}>
                <tr style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <td rowSpan={2} style={{ textAlign: "right", padding: "5px 12px", color: "#fff", fontSize: 13, fontWeight: 400, verticalAlign: "middle" }}>{r.name}</td>
                  <td style={{ textAlign: "center", padding: "4px 8px", color: "#fff", fontSize: 12, fontWeight: 400 }}>L</td>
                  <td rowSpan={2} style={{ textAlign: "center", padding: "4px 12px", color: "#fff", fontSize: 14 }}></td>
                  <td style={{ textAlign: "center", padding: "4px 12px", color: "#fff", fontSize: 14 }}>{r.outL}</td>
                  <td rowSpan={2} style={{ textAlign: "center", padding: "4px 12px", color: "#fff", fontSize: 14, fontWeight: 500 }}>{r.net}</td>
                  <td rowSpan={2} style={{ textAlign: "center", padding: "4px 12px", color: "#DAF2D0", fontSize: 14, fontWeight: 500, background: "rgba(218,242,208,0.05)" }}>{r.pct}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: "center", padding: "4px 8px", color: "#fff", fontSize: 12, fontWeight: 400 }}>S</td>
                  <td style={{ textAlign: "center", padding: "4px 12px", color: "#fff", fontSize: 14 }}>{r.outS}</td>
                </tr>
              </React.Fragment>
            ))}
            <tr style={{ borderTop: "2px solid rgba(255,255,255,0.15)" }}>
              <td colSpan={2} style={{ padding: "6px 12px" }}></td>
              <td colSpan={2} style={{ textAlign: "right", padding: "6px 12px", color: "#fff", fontSize: 13, fontWeight: 500 }}>Open Interest (OI) =</td>
              <td style={{ textAlign: "center", padding: "6px 12px", color: "#fff", fontSize: 14, fontWeight: 600 }}>325</td>
              <td style={{ textAlign: "center", padding: "6px 12px" }}></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ marginBottom: 10, fontSize: 15 }}><span style={{ color: "#fff", fontWeight: 600 }}>Percentile Band</span>: Each band represents 0th – 100th percentile of 52-week lookback.</div>
      <div style={{ marginBottom: 10, fontSize: 15 }}><span style={{ color: "#fff", fontWeight: 600 }}>Net %OI Positioning</span>: Over 2SD to the right = extreme "net long", to the left = extreme "net short" (more potential convergence).</div>
      <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 14 }}>Within 1SD (68%) ranges from 16th – 84th. At 2SD is at 5th and 95th percentile(s).</div>
    </div>
  );
}

/* ─── Helpers ───────────────────────────────────────────────────── */
const bd = "1px solid rgba(184,212,208,0.04)";
const gbd = "2px solid rgba(184,212,208,0.18)";
const ce = { padding: "6px 8px", color: TEAL, fontSize: 15, fontFamily: "'DM Sans', sans-serif", fontWeight: 400 };
const nm = { textAlign: "right", fontVariantNumeric: "tabular-nums" };
function fmtNum(v) { if (v == null) return ""; return v.toLocaleString("en-US", { maximumFractionDigits: 0 }); }
function fmtSigned(v) { if (v == null) return ""; return (v > 0 ? "+" : "") + v.toLocaleString("en-US", { maximumFractionDigits: 0 }); }
function fmtDollar(v) {
  if (v == null) return "";
  const m = v / 1e6;
  if (Math.abs(m) >= 1000) return `$${m.toLocaleString("en-US", { maximumFractionDigits: 0 })}M`;
  if (Math.abs(m) >= 1) return `$${m.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}M`;
  return `$${m.toFixed(3)}M`;
}
